import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Clock } from "lucide-react";

const values = [
  {
    title: "Discretion",
    description:
      "Every engagement is handled with absolute confidentiality. Your privacy is not a policy — it is a principle.",
  },
  {
    title: "Excellence",
    description:
      "We hold ourselves to the highest standard in every detail, from the first enquiry to the final experience.",
  },
  {
    title: "Personalisation",
    description:
      "No two clients are the same. We craft every service around your preferences, lifestyle, and vision.",
  },
  {
    title: "Presence",
    description:
      "Available 24 hours a day across Abuja, Lagos, and Calabar — wherever you are, we are close.",
  },
];

const openRoles = [
  { title: "Lifestyle Manager", location: "Abuja", type: "Full-time", slug: "lifestyle-manager" },
  { title: "Events Coordinator", location: "Lagos", type: "Full-time", slug: "events-coordinator" },
  { title: "Client Relations Associate", location: "Calabar", type: "Full-time", slug: "client-relations-associate" },
];

const partnerCategories = [
  {
    heading: "Luxury Hospitality",
    body: "We work alongside leading five-star hotels and private villa estates to provide our clients with seamless accommodation experiences.",
  },
  {
    heading: "Private Aviation",
    body: "Our aviation partners offer access to private jet charters, VIP terminal lounges, and bespoke in-flight services across Nigeria and beyond.",
  },
  {
    heading: "Culinary & Dining",
    body: "Through exclusive arrangements with internationally recognised chefs and private dining venues, we bring the finest cuisine directly to our clients.",
  },
  {
    heading: "Wellness & Retreat",
    body: "We partner with premium spas, wellness retreats, and medical concierge providers to support the holistic wellbeing of our members.",
  },
];

export default function AboutPage() {
  const location = useLocation();

  // Scroll to hash on mount / hash change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 80);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location]);

  return (
    <main style={ { backgroundColor: "#0b1a14" }}>
      {/* Hero */}
      <header className="relative pt-40 pb-24 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <span className="text-[#C4A77D] text-xs tracking-[0.4em] uppercase">
            Our Story
          </span>
          <h1
            className="text-5xl md:text-7xl text-white font-light mt-4 leading-tight"
            style={ { fontFamily: "'Playfair Display', serif" }}
          >
            About Raha-ídúù
          </h1>
          <p className="mt-6 text-gray-400 text-lg font-light leading-relaxed max-w-2xl">
            We are Nigeria's foremost luxury concierge service, dedicated to
            curating extraordinary experiences for those who demand nothing less
            than the exceptional.
          </p>
        </div>
      </header>

      {/* Story */}
      <section className="py-24 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-16 items-start">
          <div>
            <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">
              Who We Are
            </span>
            <h2
              className="text-3xl text-white mt-3 mb-6 font-light"
              style={ { fontFamily: "'Playfair Display', serif" }}
            >
              Born in Nigeria.<br />Built for Excellence.
            </h2>
            <p className="text-gray-400 text-sm font-light leading-relaxed mb-4">
              Raha-ídúù was founded with a singular conviction: that Nigerians
              deserve access to world-class lifestyle management, delivered with
              the warmth and understanding of those who know this country best.
            </p>
            <p className="text-gray-400 text-sm font-light leading-relaxed">
              Operating across Abuja, Lagos, and Calabar, our team of dedicated
              lifestyle managers brings decades of combined experience in luxury
              hospitality, private events, and bespoke travel — always in service
              of one objective: your complete satisfaction.
            </p>
          </div>
          <div className="space-y-6">
            {[
              ["3+", "Cities across Nigeria"],
              ["24/7", "Around-the-clock support"],
              ["100%", "Commitment to discretion"],
            ].map(([stat, label]) => (
              <div
                key={stat}
                className="flex items-center gap-6 p-6 border border-white/10 bg-white/[0.02]"
              >
                <span
                  className="text-4xl text-[#C4A77D] flex-shrink-0"
                  style={ { fontFamily: "'Playfair Display', serif" }}
                >
                  {stat}
                </span>
                <span className="text-gray-400 text-sm font-light">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 bg-[#081210] border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">
              What Guides Us
            </span>
            <h2
              className="text-3xl md:text-4xl text-white mt-3 font-light"
              style={ { fontFamily: "'Playfair Display', serif" }}
            >
              Our Values
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-px bg-white/5">
            {values.map((v) => (
              <div key={v.title} className="p-8 bg-[#081210]">
                <div className="w-6 h-px bg-[#C4A77D] mb-4"></div>
                <h3
                  className="text-white text-lg font-light mb-3"
                  style={ { fontFamily: "'Playfair Display', serif" }}
                >
                  {v.title}
                </h3>
                <p className="text-gray-500 text-sm font-light leading-relaxed">
                  {v.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section id="careers" className="py-24 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">
            Join the Team
          </span>
          <h2
            className="text-3xl md:text-4xl text-white mt-3 mb-4 font-light"
            style={ { fontFamily: "'Playfair Display', serif" }}
          >
            Careers
          </h2>
          <p className="text-gray-400 text-sm font-light leading-relaxed max-w-xl mb-12">
            We look for individuals who share our passion for exceptional service
            and take genuine pride in the experiences they create. If that is
            you, we would love to hear from you.
          </p>

          <div className="space-y-3 mb-12">
            {openRoles.map((role) => (
              <Link
                key={role.title}
                to={`/careers/${role.slug}`}
                className="flex items-center justify-between p-6 border border-white/10 bg-white/[0.02] hover:border-[#C4A77D]/30 transition-colors duration-300 group"
              >
                <div>
                  <p className="text-white text-sm font-light group-hover:text-[#C4A77D] transition-colors">{role.title}</p>
                  <p className="text-gray-500 text-xs mt-1 tracking-wide flex items-center gap-3">
                    <span className="flex items-center gap-1"><MapPin size={11} className="text-[#C4A77D]" />{role.location}</span>
                    <span className="flex items-center gap-1"><Clock size={11} className="text-[#C4A77D]" />{role.type}</span>
                  </p>
                </div>
                <ArrowRight className="text-[#C4A77D] w-4 h-4 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3 p-6 border border-[#C4A77D]/20 bg-[#C4A77D]/5">
            <Mail className="text-[#C4A77D] w-5 h-5 flex-shrink-0" />
            <p className="text-gray-400 text-sm font-light">
              Don't see a suitable role?{" "}
              <a
                href="mailto:careers@raha-iduu.com"
                className="text-[#C4A77D] hover:text-white transition-colors"
              >
                Send us your CV
              </a>{" "}
              — we're always open to exceptional talent.
            </p>
          </div>
        </div>
      </section>

      {/* Partnerships */}
      <section id="partnerships" className="py-24 px-6 bg-[#050d09]">
        <div className="max-w-4xl mx-auto">
          <span className="text-[#C4A77D] text-xs tracking-[0.3em] uppercase">
            Work With Us
          </span>
          <h2
            className="text-3xl md:text-4xl text-white mt-3 mb-4 font-light"
            style={ { fontFamily: "'Playfair Display', serif" }}
          >
            Partnerships
          </h2>
          <p className="text-gray-400 text-sm font-light leading-relaxed max-w-xl mb-12">
            We collaborate with best-in-class providers who share our commitment
            to excellence. Our partnerships are built on mutual trust and the
            pursuit of unparalleled client experiences.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {partnerCategories.map((p) => (
              <div
                key={p.heading}
                className="p-7 border border-white/10 bg-white/[0.02]"
              >
                <h3
                  className="text-[#C4A77D] text-sm font-light tracking-wide mb-3"
                  style={ { fontFamily: "'Playfair Display', serif" }}
                >
                  {p.heading}
                </h3>
                <p className="text-gray-500 text-sm font-light leading-relaxed">
                  {p.body}
                </p>
              </div>
            ))}
          </div>

          <div className="p-8 border border-[#C4A77D]/20 text-center">
            <p className="text-gray-400 text-sm font-light mb-4">
              Interested in a partnership with Raha-ídúù?
            </p>
            <a
              href="mailto:partnerships@raha-iduu.com"
              className="inline-flex items-center gap-2 px-8 py-3 border border-[#C4A77D] text-[#C4A77D] text-xs uppercase tracking-wider hover:bg-[#C4A77D] hover:text-black transition-all duration-300"
            >
              <Mail className="w-3.5 h-3.5" />
              Get in Touch
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
